import Accordion from '@repo/ui/accordion'
import Badge from '@repo/ui/badge'
import { Skeleton } from '@repo/ui/skeleton'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'

import { getCenterTripBookingsTrips } from '../../../api/centers/get-center-tripbookings-trips'
import TransportPicto from '../../../components/elements/transport-picto'
import { TripTripBookingsAccordion } from '../../../components/pages/centers/center-trips.ui'
import {
  CenterTripFiltersType,
  CenterTripsFilter
} from '../../../components/pages/centers/center-trips-filter'
import { TripCenterEnrollments } from '../../../components/pages/centers/trip-center-enrollments.ui'
import { tripTypeMapping } from '../../../lib/types/enums/trip/trip-type.enum'
import { TripProperties } from '../../../lib/types/models/trip/properties.trip'

type CenterTripsProps = {
  centerId: number
}

export function CenterTrips({ centerId }: CenterTripsProps) {
  const [tripAccordion, setTripAccordion] = useState<Record<number, boolean>>()

  const [filters, setFilters] = useState<CenterTripFiltersType>({
    directionalities: [],
    tripIds: []
  })

  const { data, isPending } = useQuery({
    queryKey: ['center-trip-bookings-trips', centerId, filters],
    queryFn: () => getCenterTripBookingsTrips(centerId, filters)
  })

  return (
    <div className={'flex flex-col gap-5'}>
      <div />

      <CenterTripsFilter
        centerId={centerId}
        filters={filters}
        onChange={(filters) => {
          setFilters(filters)
          setTripAccordion({})
        }}
      />

      {isPending
        ? Array.from({ length: 3 }).map((_, index) => (
            <Skeleton key={index} className={'h-20 w-full'} />
          ))
        : data?.map((trip) => {
            const tripTitle = [
              TripProperties.getTripLightLabel(trip),
              trip.transportMeans && TripProperties.handleTransportMeansName(trip.transportMeans)
            ]
              .filter(Boolean)
              .join(' - ')

            return (
              <Accordion
                key={trip.id}
                variant={'outline'}
                className={'rounded-lg'}
                open={tripAccordion?.[trip.id] ?? false}
                onToggle={() =>
                  setTripAccordion((prev) => ({ ...prev, [trip.id]: !prev?.[trip.id] }))
                }
                title={
                  <div className={'flex items-center flex-row flex-wrap gap-2'}>
                    <Badge
                      text={tripTypeMapping[trip.type].label}
                      variant={tripTypeMapping[trip.type].variant}
                    />

                    {trip.transportMeans?.picto && (
                      <TransportPicto picto={trip.transportMeans.picto} />
                    )}

                    <Typography variant={'text'}>{tripTitle}</Typography>
                  </div>
                }
              >
                {trip.transportMeans ? (
                  <TripTripBookingsAccordion
                    centerId={centerId}
                    trip={trip}
                    filters={filters}
                    enabled={tripAccordion?.[trip.id] ?? false}
                  />
                ) : (
                  <TripCenterEnrollments centerId={centerId} tripId={trip.id} />
                )}
              </Accordion>
            )
          })}
    </div>
  )
}
